import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { getLocalDateInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import {
  MAX_DAYS_IN_FUTURE,
  OTB_TABS,
  SEARCHES_TABS,
} from 'lumen/utils/constants/market-insight-constants';

type OtbTab = 'my-otb' | 'wow-change';

type RefDaysAgo = {
  id: string;
  label: string;
};

type SearchTab = 'flights' | 'flightsgds' | 'hotels' | 'hotelsgds';

export default class MiSelectionsService extends Service {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked dateAggregation: DateAggregationType | undefined = undefined;
  @tracked minAllowedDay: Dayjs | undefined = undefined;
  @tracked otbTab: OtbTab | undefined = undefined;
  @tracked refDaysAgoDatesInAPIFormat: Record<string, string> = {};
  @tracked searchesDetailsItem: SearchTab | undefined = undefined;
  @tracked searchesTab: SearchTab | undefined = undefined;
  @tracked selectedRefDaysAgo: RefDaysAgo | undefined = undefined;
  @tracked showAllEvents: boolean = false;

  get defaultDateFormatWithDOW(): string | undefined {
    return this.dates.partialDateFormat?.['WEEKDAY_AND_DATE_LONG'];
  }

  get isDailyOverview(): boolean {
    return this.dateAggregation === DATE_AGGREGATION.DAY;
  }

  get isFlightBusinessSearchesDetailsItemActive(): boolean {
    return this.searchesDetailsItem === SEARCHES_TABS.FLIGHTSGDS;
  }

  get isFlightBusinessSearchesTabActive(): boolean {
    return this.searchesTab === SEARCHES_TABS.FLIGHTSGDS;
  }

  get isFlightSearchesDetailsItemActive(): boolean {
    return this.searchesDetailsItem === SEARCHES_TABS.FLIGHTS;
  }

  get isFlightSearchesTabActive(): boolean {
    return this.searchesTab === SEARCHES_TABS.FLIGHTS;
  }

  get isHotelBusinessSearchesDetailsItemActive(): boolean {
    return this.searchesDetailsItem === SEARCHES_TABS.HOTELSGDS;
  }

  get isHotelBusinessSearchesTabActive(): boolean {
    return this.searchesTab === SEARCHES_TABS.HOTELSGDS;
  }

  get isHotelSearchesDetailsItemActive(): boolean {
    return this.searchesDetailsItem === SEARCHES_TABS.HOTELS;
  }

  get isHotelSearchesTabActive(): boolean {
    return this.searchesTab === SEARCHES_TABS.HOTELS;
  }

  get isOTBTabActive(): boolean {
    return this.otbTab === OTB_TABS.MY_OTB;
  }

  get isWeeklyOverview(): boolean {
    return this.dateAggregation === DATE_AGGREGATION.WEEK;
  }

  get isWoWTabActive(): boolean {
    return this.otbTab === OTB_TABS.WOW_CHANGE;
  }

  get maxAllowedDay(): Dayjs | undefined {
    const { minAllowedDay } = this;

    if (minAllowedDay) {
      return minAllowedDay.add(MAX_DAYS_IN_FUTURE, 'day');
    }

    return undefined;
  }

  getEndDateInRange(endDate: Dayjs): Dayjs {
    const { maxAllowedDay } = this;

    if (maxAllowedDay && endDate.isAfter(maxAllowedDay, 'day')) {
      return maxAllowedDay;
    }

    return endDate;
  }

  getStartDateInRange(startDate: Dayjs): Dayjs {
    const { minAllowedDay } = this;

    if (minAllowedDay && startDate.isBefore(minAllowedDay, 'day')) {
      return minAllowedDay;
    }

    return startDate;
  }

  setRefDaysAgoDatesInAPIFormat(): void {
    const today = getLocalDateInBrowserTimezone(
      this.hotelAndMarketService.timezone,
    );

    const refDaysAgoDatesInAPIFormat: Record<string, string> = {};
    const numDaysAgos = [1, 3, 7, 14];

    numDaysAgos.forEach((numDaysAgo) => {
      const date = today.subtract(numDaysAgo, 'days').format('YYYY-MM-DD');
      refDaysAgoDatesInAPIFormat[numDaysAgo] = date;
    });

    this.refDaysAgoDatesInAPIFormat = refDaysAgoDatesInAPIFormat;
  }
}

declare module '@ember/service' {
  interface Registry {
    'mi-selections': MiSelectionsService;
  }
}
